import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBrand } from '@/brand/BrandProvider'
import { apiClient } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'

export function ForgotPassword() {
  const { theme } = useBrand()
  // PRODUCT-IDENTITY-1 (I3): preserve QoE's exact pre-existing text ("QoE
  // Platform"), same reasoning as TosGate.tsx — see that file's comment.
  const productName = PRODUCT_KEY === 'qoe' ? 'QoE Platform' : PRODUCT.headline

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await apiClient.post('/auth/forgot-password', { email })
    } catch {
      // Endpoint always returns {ok: true} — a network error is the only
      // failure mode here, and we still show the generic message below.
    } finally {
      setLoading(false)
      setSubmitted(true)
    }
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      <img
        src={`/api/brand/${theme.key}/logo`}
        alt={theme.name}
        className="h-10 mb-6 object-contain"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />

      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1">
          <CardTitle
            className="text-2xl font-bold"
            style={{ color: 'var(--color-primary)' }}
          >
            Forgot password
          </CardTitle>
          <CardDescription>{theme.name} · {productName}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {submitted ? (
            <Alert>
              <AlertDescription>
                If an account exists for that email, a password reset link is on its way.
                Check your inbox — the link expires in 1 hour.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" placeholder="you@firm.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  required autoComplete="email" autoFocus
                />
              </div>
              <Button
                type="submit" className="w-full" disabled={loading}
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                {loading ? 'Sending…' : 'Send reset link'}
              </Button>
            </form>
          )}

          <div className="text-center text-sm text-gray-500 pt-2">
            <Link to="/login" className="font-medium underline"
                  style={{ color: 'var(--color-primary)' }}>
              ← Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
